import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

const ADMIN_UID = 'yM6RnvyZ7VbcWxXwuHgPKsegVN22';

const TABS = ['Overview', 'License Review', 'Users', 'Disputed Hours', 'Active Jobs'];

function AdminDashboard() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    totalWorkers: 0,
    totalContractors: 0,
    activeJobs: 0,
    pendingLicenses: 0,
    disputedHours: 0,
    totalConfirmedHours: 0,
    platformRevenue: 0,
  });

  const [pendingLicenses, setPendingLicenses] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [licenseNote, setLicenseNote] = useState('');
  const [processingLicense, setProcessingLicense] = useState(false);

  const [workers, setWorkers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userTab, setUserTab] = useState('workers');

  const [disputes, setDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolvedHours, setResolvedHours] = useState(8);
  const [resolutionNote, setResolutionNote] = useState('');
  const [processingDispute, setProcessingDispute] = useState(false);

  const [activeJobs, setActiveJobs] = useState([]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) { navigate('/'); return; }
      if (user.uid !== ADMIN_UID) { navigate('/dashboard'); return; }
      setAuthorized(true);
      setChecking(false);
      loadAllData();
    });
    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadWorkers(),
        loadContractors(),
        loadJobs(),
        loadLicenses(),
        loadDisputes(),
      ]);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadWorkers = async () => {
    const snap = await getDocs(collection(db, 'workerProfiles'));
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setWorkers(list);
    setStats(prev => ({ ...prev, totalWorkers: list.length }));
    return list;
  };

  const loadContractors = async () => {
    const snap = await getDocs(collection(db, 'contractorProfiles'));
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setContractors(list);
    setStats(prev => ({ ...prev, totalContractors: list.length }));
    return list;
  };

  const loadJobs = async () => {
    const snap = await getDocs(collection(db, 'jobRequests'));
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const active = all.filter(j => j.status === 'accepted');
    setActiveJobs(active);
    const confSnap = await getDocs(collection(db, 'jobConfirmations'));
    const confs = confSnap.docs.map(d => d.data());
    const verified = confs.filter(c => c.status === 'verified');
    const totalHours = verified.reduce((sum, c) => sum + (c.confirmedHours || 0), 0);
    const revenue = verified.reduce((sum, c) => {
      const rate = c.contractorRate || 0;
      const hours = c.confirmedHours || 0;
      return sum + (hours * rate * 0.15);
    }, 0);
    setStats(prev => ({
      ...prev,
      activeJobs: active.length,
      totalConfirmedHours: totalHours,
      platformRevenue: Math.round(revenue),
    }));
    return active;
  };

  const loadLicenses = async () => {
    const snap = await getDocs(collection(db, 'workerProfiles'));
    const pending = [];
    snap.docs.forEach(d => {
      const data = d.data();
      if (data.tradeProfiles) {
        data.tradeProfiles.forEach(tp => {
          if (tp.licenseStatus === 'pending' && tp.licenseDoc) {
            pending.push({
              workerId: d.id,
              workerName: data.name,
              workerEmail: data.email,
              trade: tp.trade,
              level: tp.level,
              licenseNumber: tp.licenseNumber,
              licenseType: tp.licenseType,
              licenseState: tp.licenseState,
              licenseExpiry: tp.licenseExpiry,
              licenseDoc: tp.licenseDoc,
              licenseBoard: tp.licenseBoard,
              submittedAt: tp.submittedAt,
              tradeId: tp.id,
            });
          }
        });
      }
    });
    setPendingLicenses(pending);
    setStats(prev => ({ ...prev, pendingLicenses: pending.length }));
    return pending;
  };

  const loadDisputes = async () => {
    const snap = await getDocs(collection(db, 'jobConfirmations'));
    const disputed = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(c => c.status === 'disputed');
    setDisputes(disputed);
    setStats(prev => ({ ...prev, disputedHours: disputed.length }));
    return disputed;
  };

  const handleLicenseDecision = async (decision) => {
    if (!selectedLicense) return;
    setProcessingLicense(true);
    try {
      const workerRef = doc(db, 'workerProfiles', selectedLicense.workerId);
      const workerSnap = await getDoc(workerRef);
      const workerData = workerSnap.data();
      const updatedProfiles = workerData.tradeProfiles.map(tp => {
        if (tp.id === selectedLicense.tradeId) {
          return {
            ...tp,
            licenseStatus: decision === 'approve' ? 'verified' : 'rejected',
            verifiedAt: decision === 'approve' ? new Date().toISOString() : null,
            rejectionNote: decision === 'reject' ? licenseNote : null,
          };
        }
        return tp;
      });
      await updateDoc(workerRef, { tradeProfiles: updatedProfiles });
      setPendingLicenses(prev => prev.filter(l => l.tradeId !== selectedLicense.tradeId));
      setSelectedLicense(null);
      setLicenseNote('');
      setStats(prev => ({ ...prev, pendingLicenses: prev.pendingLicenses - 1 }));
    } catch (err) { console.error(err); }
    setProcessingLicense(false);
  };

  const handleResolveDispute = async () => {
    if (!selectedDispute || !resolvedHours) return;
    setProcessingDispute(true);
    try {
      const confRef = doc(db, 'jobConfirmations', selectedDispute.id);
      await updateDoc(confRef, {
        status: 'verified',
        confirmedHours: parseInt(resolvedHours),
        adminResolved: true,
        adminNote: resolutionNote,
        resolvedAt: new Date(),
        contractorConfirmed: true,
        contractorHours: parseInt(resolvedHours),
      });
      setDisputes(prev => prev.filter(d => d.id !== selectedDispute.id));
      setSelectedDispute(null);
      setResolutionNote('');
      setResolvedHours(8);
      setStats(prev => ({ ...prev, disputedHours: prev.disputedHours - 1 }));
    } catch (err) { console.error(err); }
    setProcessingDispute(false);
  };

  const handleDeactivateWorker = async (workerId) => {
    if (!window.confirm('Deactivate this worker? They will not appear in searches.')) return;
    try {
      await updateDoc(doc(db, 'workerProfiles', workerId), { deactivated: true });
      setWorkers(prev => prev.map(w => w.id === workerId ? { ...w, deactivated: true } : w));
    } catch (err) { console.error(err); }
  };

  const handleReactivateWorker = async (workerId) => {
    try {
      await updateDoc(doc(db, 'workerProfiles', workerId), { deactivated: false });
      setWorkers(prev => prev.map(w => w.id === workerId ? { ...w, deactivated: false } : w));
    } catch (err) { console.error(err); }
  };

  const formatDate = (val) => {
    if (!val) return 'N/A';
    try {
      const d = val?.toDate ? val.toDate() : new Date(val);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return 'N/A'; }
  };

  const filteredWorkers = workers.filter(w =>
    !userSearch ||
    w.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    w.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredContractors = contractors.filter(c =>
    !userSearch ||
    c.companyName?.toLowerCase().includes(userSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (checking) return <div style={styles.loadingContainer}><p>Checking access...</p></div>;
  if (!authorized) return null;

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <div style={styles.navLeft}>
          <h1 style={styles.logo}>TradePower</h1>
          <span style={styles.adminBadge}>Admin</span>
        </div>
        <div style={styles.navRight}>
          <span style={styles.navInfo}>Logged in as Admin</span>
          <button style={styles.backButton} onClick={() => navigate('/dashboard')}>
            Back to App
          </button>
        </div>
      </div>

      <div style={styles.content}>
        <div style={styles.pageHeader}>
          <h2 style={styles.pageTitle}>Admin Dashboard</h2>
          <button style={styles.refreshBtn} onClick={loadAllData}>Refresh Data</button>
        </div>

        <div style={styles.tabNav}>
          {TABS.map(tab => (
            <button
              key={tab}
              style={activeTab === tab ? styles.tabBtnActive : styles.tabBtn}
              onClick={() => setActiveTab(tab)}>
              {tab}
              {tab === 'License Review' && stats.pendingLicenses > 0 && (
                <span style={styles.tabBadge}>{stats.pendingLicenses}</span>
              )}
              {tab === 'Disputed Hours' && stats.disputedHours > 0 && (
                <span style={styles.tabBadgeRed}>{stats.disputedHours}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={styles.loadingBox}><p>Loading data...</p></div>
        ) : (
          <>
            {/* OVERVIEW */}
            {activeTab === 'Overview' && (
              <div style={styles.tabContent}>
                <div style={styles.statsGrid}>
                  <div style={styles.statCard}>
                    <span style={styles.statIcon}>👷</span>
                    <span style={styles.statValue}>{stats.totalWorkers}</span>
                    <span style={styles.statLabel}>Total Workers</span>
                  </div>
                  <div style={styles.statCard}>
                    <span style={styles.statIcon}>🏗️</span>
                    <span style={styles.statValue}>{stats.totalContractors}</span>
                    <span style={styles.statLabel}>Total Contractors</span>
                  </div>
                  <div style={styles.statCard}>
                    <span style={styles.statIcon}>📋</span>
                    <span style={styles.statValue}>{stats.activeJobs}</span>
                    <span style={styles.statLabel}>Active Jobs</span>
                  </div>
                  <div style={styles.statCard}>
                    <span style={styles.statIcon}>⏰</span>
                    <span style={styles.statValue}>{stats.totalConfirmedHours}</span>
                    <span style={styles.statLabel}>Confirmed Hours</span>
                  </div>
                  <div style={{ ...styles.statCard, ...styles.revenueCard }}>
                    <span style={styles.statIcon}>💰</span>
                    <span style={{ ...styles.statValue, color: '#28a745' }}>
                      ${stats.platformRevenue.toLocaleString()}
                    </span>
                    <span style={styles.statLabel}>Platform Revenue (15%)</span>
                  </div>
                  <div style={stats.pendingLicenses > 0 ? { ...styles.statCard, ...styles.alertCard } : styles.statCard}>
                    <span style={styles.statIcon}>🏛️</span>
                    <span style={styles.statValue}>{stats.pendingLicenses}</span>
                    <span style={styles.statLabel}>Pending Licenses</span>
                    {stats.pendingLicenses > 0 && (
                      <button style={styles.statActionBtn} onClick={() => setActiveTab('License Review')}>
                        Review Now
                      </button>
                    )}
                  </div>
                  <div style={stats.disputedHours > 0 ? { ...styles.statCard, ...styles.alertCard } : styles.statCard}>
                    <span style={styles.statIcon}>⚠️</span>
                    <span style={styles.statValue}>{stats.disputedHours}</span>
                    <span style={styles.statLabel}>Disputed Hours</span>
                    {stats.disputedHours > 0 && (
                      <button style={styles.statActionBtn} onClick={() => setActiveTab('Disputed Hours')}>
                        Resolve Now
                      </button>
                    )}
                  </div>
                </div>

                <div style={styles.overviewSection}>
                  <h3 style={styles.overviewTitle}>Recent Workers</h3>
                  {workers.slice(0, 5).map(w => (
                    <div key={w.id} style={styles.overviewRow}>
                      <span style={styles.overviewName}>{w.name || 'Unnamed'}</span>
                      <span style={styles.overviewEmail}>{w.email}</span>
                      <span style={styles.overviewTrades}>{w.trades?.join(', ') || 'No trades'}</span>
                      <span style={w.deactivated ? styles.inactivePill : styles.activePill}>
                        {w.deactivated ? 'Inactive' : 'Active'}
                      </span>
                    </div>
                  ))}
                </div>

                <div style={styles.overviewSection}>
                  <h3 style={styles.overviewTitle}>Recent Contractors</h3>
                  {contractors.slice(0, 5).map(c => (
                    <div key={c.id} style={styles.overviewRow}>
                      <span style={styles.overviewName}>{c.companyName || c.name || 'Unnamed'}</span>
                      <span style={styles.overviewEmail}>{c.email}</span>
                      <span style={styles.overviewTrades}>{c.licenseNumber || 'No license'}</span>
                      <span style={styles.activePill}>Active</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* LICENSE REVIEW */}
            {activeTab === 'License Review' && (
              <div style={styles.tabContent}>
                <h3 style={styles.sectionTitle}>Pending License Verifications</h3>
                <p style={styles.sectionSubtitle}>
                  Review and approve or reject contractor license uploads.
                  Workers go live immediately — approval unlocks the Licensed Contractor badge.
                </p>
                {pendingLicenses.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p style={styles.emptyIcon}>✅</p>
                    <p style={styles.emptyTitle}>All clear!</p>
                    <p style={styles.emptyText}>No pending license reviews.</p>
                  </div>
                ) : (
                  <div style={styles.licenseGrid}>
                    <div style={styles.licenseList}>
                      {pendingLicenses.map((lic, idx) => (
                        <div
                          key={idx}
                          style={selectedLicense?.tradeId === lic.tradeId
                            ? { ...styles.licenseItem, ...styles.licenseItemSelected }
                            : styles.licenseItem}
                          onClick={() => { setSelectedLicense(lic); setLicenseNote(''); }}>
                          <div style={styles.licenseItemHeader}>
                            <span style={styles.licenseItemName}>{lic.workerName}</span>
                            <span style={styles.pendingBadge}>⏳ Pending</span>
                          </div>
                          <p style={styles.licenseItemTrade}>{lic.trade} — {lic.level}</p>
                          <p style={styles.licenseItemDate}>Submitted: {formatDate(lic.submittedAt)}</p>
                        </div>
                      ))}
                    </div>

                    {selectedLicense ? (
                      <div style={styles.licenseDetail}>
                        <h4 style={styles.licenseDetailTitle}>{selectedLicense.workerName}</h4>
                        <p style={styles.licenseDetailEmail}>{selectedLicense.workerEmail}</p>
                        <div style={styles.licenseInfoGrid}>
                          <div style={styles.licenseInfoItem}>
                            <span style={styles.licenseInfoLabel}>Trade</span>
                            <span style={styles.licenseInfoValue}>{selectedLicense.trade}</span>
                          </div>
                          <div style={styles.licenseInfoItem}>
                            <span style={styles.licenseInfoLabel}>Level</span>
                            <span style={styles.licenseInfoValue}>{selectedLicense.level}</span>
                          </div>
                          <div style={styles.licenseInfoItem}>
                            <span style={styles.licenseInfoLabel}>License #</span>
                            <span style={styles.licenseInfoValue}>{selectedLicense.licenseNumber || 'Not provided'}</span>
                          </div>
                          <div style={styles.licenseInfoItem}>
                            <span style={styles.licenseInfoLabel}>State</span>
                            <span style={styles.licenseInfoValue}>{selectedLicense.licenseState}</span>
                          </div>
                          <div style={styles.licenseInfoItem}>
                            <span style={styles.licenseInfoLabel}>Type</span>
                            <span style={styles.licenseInfoValue}>{selectedLicense.licenseType || 'Not provided'}</span>
                          </div>
                          <div style={styles.licenseInfoItem}>
                            <span style={styles.licenseInfoLabel}>Expiry</span>
                            <span style={styles.licenseInfoValue}>{selectedLicense.licenseExpiry || 'Not provided'}</span>
                          </div>
                          <div style={styles.licenseInfoItem}>
                            <span style={styles.licenseInfoLabel}>Board</span>
                            <span style={styles.licenseInfoValue}>{selectedLicense.licenseBoard}</span>
                          </div>
                          <div style={styles.licenseInfoItem}>
                            <span style={styles.licenseInfoLabel}>Submitted</span>
                            <span style={styles.licenseInfoValue}>{formatDate(selectedLicense.submittedAt)}</span>
                          </div>
                        </div>
                        <div style={styles.docPreviewSection}>
                          <p style={styles.docPreviewLabel}>License Document</p>
                          {selectedLicense.licenseDoc?.startsWith('data:image') ? (
                            <img src={selectedLicense.licenseDoc} alt="License document" style={styles.docPreviewImg} />
                          ) : selectedLicense.licenseDoc?.startsWith('data:application/pdf') ? (
                            <div style={styles.pdfPreview}>
                              <p style={styles.pdfText}>📄 PDF Document</p>
                              <button style={styles.viewPdfBtn} onClick={() => {
                                const win = window.open();
                                win.document.write(`<iframe src="${selectedLicense.licenseDoc}" width="100%" height="100%"></iframe>`);
                              }}>Open PDF</button>
                            </div>
                          ) : (
                            <div style={styles.noDocBox}>
                              <p style={styles.noDocText}>No document preview available</p>
                            </div>
                          )}
                        </div>
                        <div style={styles.licenseNoteSection}>
                          <label style={styles.licenseNoteLabel}>Note (required if rejecting)</label>
                          <input
                            style={styles.licenseNoteInput}
                            type="text"
                            placeholder="e.g. License number not visible, please resubmit"
                            value={licenseNote}
                            onChange={e => setLicenseNote(e.target.value)}
                          />
                        </div>
                        <div style={styles.licenseActions}>
                          <button
                            style={processingLicense ? styles.btnDisabled : styles.rejectBtn}
                            disabled={processingLicense}
                            onClick={() => {
                              if (!licenseNote.trim()) { alert('Please add a note explaining the rejection.'); return; }
                              handleLicenseDecision('reject');
                            }}>
                            {processingLicense ? 'Processing...' : 'Reject'}
                          </button>
                          <button
                            style={processingLicense ? styles.btnDisabled : styles.approveBtn}
                            disabled={processingLicense}
                            onClick={() => handleLicenseDecision('approve')}>
                            {processingLicense ? 'Processing...' : 'Approve License'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={styles.licenseDetailEmpty}>
                        <p>Select a license from the list to review</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* USERS */}
            {activeTab === 'Users' && (
              <div style={styles.tabContent}>
                <div style={styles.usersHeader}>
                  <div style={styles.userTabRow}>
                    <button
                      style={userTab === 'workers' ? styles.userTabBtnActive : styles.userTabBtn}
                      onClick={() => setUserTab('workers')}>
                      Workers ({workers.length})
                    </button>
                    <button
                      style={userTab === 'contractors' ? styles.userTabBtnActive : styles.userTabBtn}
                      onClick={() => setUserTab('contractors')}>
                      Contractors ({contractors.length})
                    </button>
                  </div>
                  <input
                    style={styles.userSearch}
                    type="text"
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                  />
                </div>

                {/* WORKERS LIST */}
                {userTab === 'workers' && (
                  <div style={styles.userList}>
                    {filteredWorkers.length === 0 ? (
                      <p style={styles.emptyText}>No workers found.</p>
                    ) : (
                      filteredWorkers.map(worker => (
                        <div key={worker.id} style={styles.userCard}>

                          {/* Top row — avatar + name + trades */}
                          <div style={styles.userCardTop}>
                            <div style={styles.userCardLeft}>
                              {worker.photoData ? (
                                <img src={worker.photoData} alt={worker.name} style={styles.userAvatar} />
                              ) : (
                                <div style={styles.userAvatarPlaceholder}>👤</div>
                              )}
                              <div>
                                <p style={styles.userName}>{worker.name || 'Unnamed'}</p>
                                <p style={styles.userEmail}>{worker.email}</p>
                                <div style={styles.userTags}>
                                  {worker.trades?.slice(0, 3).map(t => (
                                    <span key={t} style={styles.userTradeTag}>{t}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div style={styles.userActions}>
                              <span style={worker.deactivated ? styles.inactivePill : styles.activePill}>
                                {worker.deactivated ? 'Inactive' : 'Active'}
                              </span>
                              {worker.deactivated ? (
                                <button style={styles.reactivateBtn} onClick={() => handleReactivateWorker(worker.id)}>
                                  Reactivate
                                </button>
                              ) : (
                                <button style={styles.deactivateBtn} onClick={() => handleDeactivateWorker(worker.id)}>
                                  Deactivate
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Stats row */}
                          <div style={styles.userStatsRow}>
                            <div style={styles.userStatBox}>
                              <span style={styles.userStatValue}>{worker.totalJobs || 0}</span>
                              <span style={styles.userStatLabel}>Total Jobs</span>
                            </div>
                            <div style={styles.userStatBox}>
                              {worker.rating > 0 ? (
                                <>
                                  <span style={styles.starValue}>
                                    ★ {worker.rating.toFixed(1)}
                                  </span>
                                  <span style={styles.userStatLabel}>
                                    Rating ({worker.reviewCount || 0})
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span style={styles.noRating}>—</span>
                                  <span style={styles.userStatLabel}>No Rating</span>
                                </>
                              )}
                            </div>
                            <div style={styles.userStatBox}>
                              <span style={
                                (worker.cancellations || 0) > 2
                                  ? { ...styles.userStatValue, color: '#dc3545' }
                                  : styles.userStatValue
                              }>
                                {worker.cancellations || 0}
                              </span>
                              <span style={styles.userStatLabel}>Cancellations</span>
                            </div>
                            <div style={styles.userStatBox}>
                              <span style={
                                (worker.lateCancellations || 0) > 1
                                  ? { ...styles.userStatValue, color: '#E94560', fontWeight: 'bold' }
                                  : styles.userStatValue
                              }>
                                {worker.lateCancellations || 0}
                              </span>
                              <span style={styles.userStatLabel}>Late Cancels</span>
                            </div>
                            <div style={styles.userStatBox}>
                              <span style={
                                (worker.noShows || 0) > 0
                                  ? { ...styles.userStatValue, color: '#dc3545', fontWeight: 'bold' }
                                  : styles.userStatValue
                              }>
                                {worker.noShows || 0}
                              </span>
                              <span style={styles.userStatLabel}>No Shows</span>
                            </div>
                            <div style={styles.userStatBox}>
                              <span style={styles.userStatValue}>${worker.hourlyRate || 0}/hr</span>
                              <span style={styles.userStatLabel}>Rate</span>
                            </div>
                            <div style={styles.userStatBox}>
                              <span style={styles.userStatValue}>{worker.zipCode || '—'}</span>
                              <span style={styles.userStatLabel}>ZIP</span>
                            </div>
                          </div>

                          {/* Auto flags */}
                          <div style={styles.userFlagsRow}>
                            {(worker.noShows || 0) > 0 && (
                              <span style={styles.flagBadgeRed}>🚫 Has No-Shows</span>
                            )}
                            {(worker.lateCancellations || 0) > 2 && (
                              <span style={styles.flagBadgeRed}>⚠️ High Late Cancels</span>
                            )}
                            {(worker.cancellations || 0) > 3 && (
                              <span style={styles.flagBadgeYellow}>⚠️ High Cancellations</span>
                            )}
                            {worker.rating > 0 && worker.rating < 3 && (
                              <span style={styles.flagBadgeRed}>⭐ Low Rating</span>
                            )}
                            {worker.rating >= 4.5 && (worker.reviewCount || 0) >= 3 && (
                              <span style={styles.flagBadgeGreen}>⭐ Top Rated</span>
                            )}
                            {(worker.totalJobs || 0) >= 10 && (worker.cancellations || 0) === 0 && (
                              <span style={styles.flagBadgeGreen}>✅ Perfect Record</span>
                            )}
                            {!worker.agreements?.independent_contractor && (
                              <span style={styles.flagBadgeYellow}>⚠️ Agreements Incomplete</span>
                            )}
                          </div>

                          {/* License tags */}
                          {worker.tradeProfiles?.some(tp => tp.requiresLicense) && (
                            <div style={styles.userLicenses}>
                              {worker.tradeProfiles.filter(tp => tp.requiresLicense).map(tp => (
                                <span
                                  key={tp.id}
                                  style={
                                    tp.licenseStatus === 'verified' ? styles.licVerifiedTag
                                    : tp.licenseStatus === 'pending' ? styles.licPendingTag
                                    : styles.licNoneTag
                                  }>
                                  {tp.trade}: {tp.licenseStatus === 'verified' ? 'Licensed'
                                    : tp.licenseStatus === 'pending' ? 'Pending' : 'No License'}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* CONTRACTORS LIST */}
                {userTab === 'contractors' && (
                  <div style={styles.userList}>
                    {filteredContractors.length === 0 ? (
                      <p style={styles.emptyText}>No contractors found.</p>
                    ) : (
                      filteredContractors.map(contractor => (
                        <div key={contractor.id} style={styles.userCard}>

                          {/* Top row */}
                          <div style={styles.userCardTop}>
                            <div style={styles.userCardLeft}>
                              <div style={styles.userAvatarPlaceholder}>🏗️</div>
                              <div>
                                <p style={styles.userName}>
                                  {contractor.companyName || contractor.name || 'Unnamed'}
                                </p>
                                <p style={styles.userEmail}>{contractor.email}</p>
                                <div style={styles.userTags}>
                                  {contractor.trades?.slice(0, 3).map(t => (
                                    <span key={t} style={styles.userTradeTag}>{t}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div style={styles.userActions}>
                              <span style={styles.activePill}>Active</span>
                            </div>
                          </div>

                          {/* Stats row */}
                          <div style={styles.userStatsRow}>
                            <div style={styles.userStatBox}>
                              <span style={styles.userStatValue}>{contractor.totalJobs || 0}</span>
                              <span style={styles.userStatLabel}>Total Jobs</span>
                            </div>
                            <div style={styles.userStatBox}>
                              {contractor.rating > 0 ? (
                                <>
                                  <span style={styles.starValue}>★ {contractor.rating.toFixed(1)}</span>
                                  <span style={styles.userStatLabel}>Rating ({contractor.reviewCount || 0})</span>
                                </>
                              ) : (
                                <>
                                  <span style={styles.noRating}>—</span>
                                  <span style={styles.userStatLabel}>No Rating</span>
                                </>
                              )}
                            </div>
                            <div style={styles.userStatBox}>
                              <span style={
                                (contractor.cancellations || 0) > 2
                                  ? { ...styles.userStatValue, color: '#dc3545' }
                                  : styles.userStatValue
                              }>
                                {contractor.cancellations || 0}
                              </span>
                              <span style={styles.userStatLabel}>Cancellations</span>
                            </div>
                            <div style={styles.userStatBox}>
                              <span style={
                                (contractor.lateCancellations || 0) > 1
                                  ? { ...styles.userStatValue, color: '#E94560', fontWeight: 'bold' }
                                  : styles.userStatValue
                              }>
                                {contractor.lateCancellations || 0}
                              </span>
                              <span style={styles.userStatLabel}>Late Cancels</span>
                            </div>
                            <div style={styles.userStatBox}>
                              <span style={styles.userStatValue}>{contractor.licenseNumber || '—'}</span>
                              <span style={styles.userStatLabel}>License #</span>
                            </div>
                            <div style={styles.userStatBox}>
                              <span style={styles.userStatValue}>{contractor.state || '—'}</span>
                              <span style={styles.userStatLabel}>State</span>
                            </div>
                          </div>

                          {/* Auto flags */}
                          <div style={styles.userFlagsRow}>
                            {(contractor.lateCancellations || 0) > 2 && (
                              <span style={styles.flagBadgeRed}>⚠️ High Late Cancels</span>
                            )}
                            {(contractor.cancellations || 0) > 3 && (
                              <span style={styles.flagBadgeYellow}>⚠️ High Cancellations</span>
                            )}
                            {contractor.rating > 0 && contractor.rating < 3 && (
                              <span style={styles.flagBadgeRed}>⭐ Low Rating</span>
                            )}
                            {contractor.rating >= 4.5 && (contractor.reviewCount || 0) >= 3 && (
                              <span style={styles.flagBadgeGreen}>⭐ Top Rated</span>
                            )}
                            {(contractor.totalJobs || 0) >= 10 && (contractor.cancellations || 0) === 0 && (
                              <span style={styles.flagBadgeGreen}>✅ Reliable Contractor</span>
                            )}
                            {!contractor.agreements?.not_employer && (
                              <span style={styles.flagBadgeYellow}>⚠️ Agreements Incomplete</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* DISPUTED HOURS */}
            {activeTab === 'Disputed Hours' && (
              <div style={styles.tabContent}>
                <h3 style={styles.sectionTitle}>Hour Disputes</h3>
                <p style={styles.sectionSubtitle}>
                  Resolve disputes between workers and contractors.
                  Your decision is final and both parties will see the resolution.
                </p>
                {disputes.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p style={styles.emptyIcon}>✅</p>
                    <p style={styles.emptyTitle}>No disputes!</p>
                    <p style={styles.emptyText}>All hour confirmations are resolved.</p>
                  </div>
                ) : (
                  <div style={styles.disputeGrid}>
                    <div style={styles.disputeList}>
                      {disputes.map(dispute => (
                        <div
                          key={dispute.id}
                          style={selectedDispute?.id === dispute.id
                            ? { ...styles.disputeItem, ...styles.disputeItemSelected }
                            : styles.disputeItem}
                          onClick={() => {
                            setSelectedDispute(dispute);
                            setResolvedHours(Math.round(((dispute.workerHours || 0) + (dispute.contractorHours || 0)) / 2));
                            setResolutionNote('');
                          }}>
                          <div style={styles.disputeItemHeader}>
                            <span style={styles.disputeItemName}>{dispute.workerName}</span>
                            <span style={styles.disputeItemDate}>{dispute.date}</span>
                          </div>
                          <p style={styles.disputeItemHours}>
                            Worker: {dispute.workerHours}h vs Contractor: {dispute.contractorHours}h
                          </p>
                          <p style={styles.disputeItemDesc}>{dispute.jobDescription}</p>
                        </div>
                      ))}
                    </div>

                    {selectedDispute ? (
                      <div style={styles.disputeDetail}>
                        <h4 style={styles.disputeDetailTitle}>Resolve Dispute</h4>
                        <p style={styles.disputeDetailDate}>Date: {selectedDispute.date}</p>
                        <p style={styles.disputeDetailDesc}>{selectedDispute.jobDescription}</p>
                        <p style={styles.disputeDetailAddr}>{selectedDispute.jobAddress}</p>
                        <div style={styles.disputeClaimsRow}>
                          <div style={styles.disputeClaimBox}>
                            <p style={styles.disputeClaimLabel}>Worker Claimed</p>
                            <p style={styles.disputeClaimHours}>{selectedDispute.workerHours}h</p>
                            {selectedDispute.workerNotes && (
                              <p style={styles.disputeClaimNote}>"{selectedDispute.workerNotes}"</p>
                            )}
                          </div>
                          <div style={styles.disputeVs}>vs</div>
                          <div style={styles.disputeClaimBox}>
                            <p style={styles.disputeClaimLabel}>Contractor Confirmed</p>
                            <p style={styles.disputeClaimHours}>{selectedDispute.contractorHours}h</p>
                            {selectedDispute.contractorNotes && (
                              <p style={styles.disputeClaimNote}>"{selectedDispute.contractorNotes}"</p>
                            )}
                          </div>
                        </div>
                        <div style={styles.resolutionSection}>
                          <p style={styles.resolutionLabel}>Final Hours Decision</p>
                          <div style={styles.hourButtons}>
                            {[4, 5, 6, 7, 8, 9, 10, 11, 12].map(h => (
                              <button
                                key={h}
                                style={resolvedHours === h ? styles.hourBtnActive : styles.hourBtn}
                                onClick={() => setResolvedHours(h)}>
                                {h}h
                              </button>
                            ))}
                          </div>
                          <div style={styles.quickResolution}>
                            <button style={styles.quickBtn} onClick={() => setResolvedHours(selectedDispute.workerHours)}>
                              Use Worker Hours ({selectedDispute.workerHours}h)
                            </button>
                            <button style={styles.quickBtn} onClick={() => setResolvedHours(selectedDispute.contractorHours)}>
                              Use Contractor Hours ({selectedDispute.contractorHours}h)
                            </button>
                            <button style={styles.quickBtn} onClick={() => setResolvedHours(Math.round(((selectedDispute.workerHours || 0) + (selectedDispute.contractorHours || 0)) / 2))}>
                              Split the Difference ({Math.round(((selectedDispute.workerHours || 0) + (selectedDispute.contractorHours || 0)) / 2)}h)
                            </button>
                          </div>
                          <label style={styles.resolutionNoteLabel}>Resolution Note (shown to both parties)</label>
                          <textarea
                            style={styles.resolutionNoteInput}
                            placeholder="e.g. Based on job records, worker was on site for 7 hours."
                            value={resolutionNote}
                            onChange={e => setResolutionNote(e.target.value)}
                            rows={3}
                          />
                          <div style={styles.resolutionFinalRow}>
                            <span style={styles.resolutionFinalLabel}>Final: {resolvedHours} hours confirmed</span>
                            <button
                              style={processingDispute ? styles.btnDisabled : styles.resolveBtn}
                              disabled={processingDispute}
                              onClick={handleResolveDispute}>
                              {processingDispute ? 'Resolving...' : 'Confirm Resolution'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={styles.disputeDetailEmpty}>
                        <p>Select a dispute to resolve</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ACTIVE JOBS */}
            {activeTab === 'Active Jobs' && (
              <div style={styles.tabContent}>
                <h3 style={styles.sectionTitle}>All Active Jobs</h3>
                <p style={styles.sectionSubtitle}>
                  {activeJobs.length} accepted job{activeJobs.length !== 1 ? 's' : ''} currently on the platform
                </p>
                {activeJobs.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p style={styles.emptyIcon}>📋</p>
                    <p style={styles.emptyTitle}>No active jobs</p>
                    <p style={styles.emptyText}>No jobs are currently accepted.</p>
                  </div>
                ) : (
                  <div style={styles.jobsList}>
                    {activeJobs.map(job => {
                      const totalDays = job.jobDays || 1;
                      const platformFee = Math.round((job.contractorRate || 0) * (job.jobDuration || 8) * totalDays * 0.15);
                      return (
                        <div key={job.id} style={styles.jobCard}>
                          <div style={styles.jobCardHeader}>
                            <div>
                              <p style={styles.jobWorkerName}>{job.workerName}</p>
                              <p style={styles.jobDesc}>{job.jobDescription}</p>
                            </div>
                            <div style={styles.jobRevenue}>
                              <span style={styles.jobRevenueLabel}>Platform fee</span>
                              <span style={styles.jobRevenueValue}>${platformFee}</span>
                            </div>
                          </div>
                          <div style={styles.jobDetails}>
                            <span style={styles.jobDetail}>📍 {job.jobAddress}</span>
                            <span style={styles.jobDetail}>📅 {job.jobDate} — {job.jobEndDate}</span>
                            <span style={styles.jobDetail}>⏰ {job.jobDuration || 8} hrs/day x {totalDays} days</span>
                            <span style={styles.jobDetail}>💰 ${job.contractorRate}/hr contractor | ${job.workerRate}/hr worker</span>
                          </div>
                          <div style={styles.jobCardFooter}>
                            <span style={styles.jobContractor}>Contractor: {job.contractorEmail}</span>
                            <span style={styles.acceptedPill}>Accepted</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  loadingContainer: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  loadingBox: { textAlign: 'center', padding: '60px', color: '#666' },
  navbar: {
    backgroundColor: '#1a1a2e', padding: '16px 32px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },
  navLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { fontSize: '22px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  adminBadge: {
    backgroundColor: '#E94560', color: 'white',
    padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
  },
  navRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  navInfo: { fontSize: '13px', color: 'rgba(255,255,255,0.6)' },
  backButton: {
    padding: '8px 16px', backgroundColor: 'transparent',
    color: 'white', border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
  },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  pageTitle: { fontSize: '26px', fontWeight: 'bold', color: '#1a1a2e' },
  refreshBtn: {
    padding: '8px 16px', backgroundColor: 'transparent', color: '#666',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
  },
  tabNav: {
    display: 'flex', gap: '8px', marginBottom: '24px',
    backgroundColor: 'white', borderRadius: '12px', padding: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flexWrap: 'wrap',
  },
  tabBtn: {
    padding: '10px 18px', backgroundColor: 'transparent', color: '#666',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px',
  },
  tabBtnActive: {
    padding: '10px 18px', backgroundColor: '#1a1a2e', color: 'white',
    border: '1px solid #1a1a2e', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px',
  },
  tabBadge: {
    backgroundColor: '#E94560', color: 'white',
    borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 'bold',
  },
  tabBadgeRed: {
    backgroundColor: '#dc3545', color: 'white',
    borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 'bold',
  },
  tabContent: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '28px',
  },
  sectionTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '6px' },
  sectionSubtitle: { fontSize: '14px', color: '#666', marginBottom: '20px' },
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '16px', marginBottom: '32px',
  },
  statCard: {
    backgroundColor: '#f8f9fa', borderRadius: '12px', padding: '20px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: '6px', textAlign: 'center', border: '1px solid #e0e0e0',
  },
  revenueCard: { border: '2px solid #28a745', backgroundColor: '#f0fff4' },
  alertCard: { border: '2px solid #E94560', backgroundColor: '#fff0f3' },
  statIcon: { fontSize: '28px' },
  statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1a1a2e' },
  statLabel: { fontSize: '12px', color: '#666', textAlign: 'center' },
  statActionBtn: {
    marginTop: '6px', padding: '6px 12px', backgroundColor: '#E94560', color: 'white',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
  },
  overviewSection: { marginBottom: '24px' },
  overviewTitle: {
    fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e',
    marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0',
  },
  overviewRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 0', borderBottom: '1px solid #f8f8f8', flexWrap: 'wrap',
  },
  overviewName: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', minWidth: '140px' },
  overviewEmail: { fontSize: '12px', color: '#666', flex: 1 },
  overviewTrades: { fontSize: '12px', color: '#999', flex: 1 },
  activePill: {
    backgroundColor: '#d4edda', color: '#155724',
    padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
  },
  inactivePill: {
    backgroundColor: '#f8d7da', color: '#721c24',
    padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
  },
  licenseGrid: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px' },
  licenseList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  licenseItem: {
    backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '14px',
    cursor: 'pointer', border: '2px solid transparent',
  },
  licenseItemSelected: { border: '2px solid #E94560', backgroundColor: '#fff0f3' },
  licenseItemHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  licenseItemName: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e' },
  pendingBadge: {
    backgroundColor: '#fff3cd', color: '#856404',
    padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold',
  },
  licenseItemTrade: { fontSize: '13px', color: '#555', marginBottom: '2px' },
  licenseItemDate: { fontSize: '11px', color: '#999' },
  licenseDetail: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '20px' },
  licenseDetailEmpty: {
    backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '40px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#999', fontSize: '14px',
  },
  licenseDetailTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  licenseDetailEmail: { fontSize: '13px', color: '#666', marginBottom: '16px' },
  licenseInfoGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' },
  licenseInfoItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  licenseInfoLabel: { fontSize: '11px', color: '#999', fontWeight: 'bold', textTransform: 'uppercase' },
  licenseInfoValue: { fontSize: '14px', color: '#1a1a2e', fontWeight: '500' },
  docPreviewSection: { marginBottom: '16px' },
  docPreviewLabel: { fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '8px' },
  docPreviewImg: {
    width: '100%', maxHeight: '300px', objectFit: 'contain',
    borderRadius: '8px', border: '1px solid #ddd', backgroundColor: '#f0f0f0',
  },
  pdfPreview: {
    backgroundColor: '#f0f0f0', borderRadius: '8px', padding: '20px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
  },
  pdfText: { fontSize: '14px', color: '#555' },
  viewPdfBtn: {
    padding: '8px 16px', backgroundColor: '#1a1a2e', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
  },
  noDocBox: { backgroundColor: '#f0f0f0', borderRadius: '8px', padding: '20px', textAlign: 'center' },
  noDocText: { fontSize: '13px', color: '#999' },
  licenseNoteSection: { marginBottom: '16px' },
  licenseNoteLabel: { fontSize: '13px', fontWeight: 'bold', color: '#555', display: 'block', marginBottom: '6px' },
  licenseNoteInput: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box',
  },
  licenseActions: { display: 'flex', gap: '10px' },
  approveBtn: {
    flex: 2, padding: '12px', backgroundColor: '#28a745', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
  },
  rejectBtn: {
    flex: 1, padding: '12px', backgroundColor: 'transparent', color: '#dc3545',
    border: '2px solid #dc3545', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
  },
  btnDisabled: {
    flex: 1, padding: '12px', backgroundColor: '#ccc', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px', fontWeight: 'bold',
  },
  usersHeader: { marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px' },
  userTabRow: { display: 'flex', gap: '8px' },
  userTabBtn: {
    padding: '8px 18px', backgroundColor: 'transparent', color: '#666',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
  },
  userTabBtnActive: {
    padding: '8px 18px', backgroundColor: '#1a1a2e', color: 'white',
    border: '1px solid #1a1a2e', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
  },
  userSearch: {
    padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '14px', width: '100%', boxSizing: 'border-box',
  },
  userList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  userCard: {
    backgroundColor: '#f8f9fa', borderRadius: '12px', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '12px',
    border: '1px solid #e0e0e0',
  },
  userCardTop: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', gap: '12px',
  },
  userCardLeft: { display: 'flex', gap: '12px', alignItems: 'flex-start', flex: 1 },
  userAvatar: { width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  userAvatarPlaceholder: {
    width: '48px', height: '48px', borderRadius: '50%',
    backgroundColor: '#ddd', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '22px', flexShrink: 0,
  },
  userName: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '2px' },
  userEmail: { fontSize: '12px', color: '#666', marginBottom: '6px' },
  userTags: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  userTradeTag: {
    backgroundColor: '#e8f4fd', color: '#2c5282',
    padding: '2px 8px', borderRadius: '8px', fontSize: '11px',
  },
  userActions: { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 },
  userStatsRow: {
    display: 'flex', gap: '8px', flexWrap: 'wrap',
    paddingTop: '12px', borderTop: '1px solid #e8e8e8',
  },
  userStatBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    backgroundColor: 'white', borderRadius: '8px', padding: '8px 14px',
    minWidth: '80px', gap: '3px', border: '1px solid #e0e0e0',
  },
  userStatValue: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e' },
  userStatLabel: {
    fontSize: '10px', color: '#999', textTransform: 'uppercase',
    fontWeight: 'bold', textAlign: 'center',
  },
  starValue: { fontSize: '15px', fontWeight: 'bold', color: '#FFB800' },
  noRating: { fontSize: '16px', color: '#ccc', fontWeight: 'bold' },
  userFlagsRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  flagBadgeRed: {
    backgroundColor: '#f8d7da', color: '#721c24',
    padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
  },
  flagBadgeYellow: {
    backgroundColor: '#fff3cd', color: '#856404',
    padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
  },
  flagBadgeGreen: {
    backgroundColor: '#d4edda', color: '#155724',
    padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
  },
  userLicenses: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  licVerifiedTag: {
    backgroundColor: '#d4edda', color: '#155724',
    padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold',
  },
  licPendingTag: {
    backgroundColor: '#fff3cd', color: '#856404',
    padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold',
  },
  licNoneTag: {
    backgroundColor: '#f8d7da', color: '#721c24',
    padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold',
  },
  deactivateBtn: {
    padding: '6px 12px', backgroundColor: 'transparent', color: '#dc3545',
    border: '1px solid #dc3545', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
  },
  reactivateBtn: {
    padding: '6px 12px', backgroundColor: '#28a745', color: 'white',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
  },
  disputeGrid: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px' },
  disputeList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  disputeItem: {
    backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '14px',
    cursor: 'pointer', border: '2px solid transparent',
  },
  disputeItemSelected: { border: '2px solid #E94560', backgroundColor: '#fff0f3' },
  disputeItemHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  disputeItemName: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e' },
  disputeItemDate: { fontSize: '11px', color: '#999' },
  disputeItemHours: { fontSize: '13px', color: '#dc3545', fontWeight: 'bold', marginBottom: '2px' },
  disputeItemDesc: { fontSize: '12px', color: '#666' },
  disputeDetail: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '20px' },
  disputeDetailEmpty: {
    backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '40px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#999', fontSize: '14px',
  },
  disputeDetailTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '6px' },
  disputeDetailDate: { fontSize: '13px', color: '#666', marginBottom: '4px' },
  disputeDetailDesc: { fontSize: '13px', color: '#555', marginBottom: '4px' },
  disputeDetailAddr: { fontSize: '12px', color: '#999', marginBottom: '16px' },
  disputeClaimsRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    marginBottom: '20px', backgroundColor: 'white', borderRadius: '10px', padding: '16px',
  },
  disputeClaimBox: { flex: 1, textAlign: 'center' },
  disputeClaimLabel: { fontSize: '12px', color: '#666', fontWeight: 'bold', marginBottom: '6px' },
  disputeClaimHours: { fontSize: '28px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  disputeClaimNote: { fontSize: '12px', color: '#999', fontStyle: 'italic' },
  disputeVs: { fontSize: '16px', color: '#dc3545', fontWeight: 'bold', flexShrink: 0 },
  resolutionSection: { backgroundColor: 'white', borderRadius: '10px', padding: '16px' },
  resolutionLabel: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '12px' },
  hourButtons: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
  hourBtn: {
    padding: '8px 14px', backgroundColor: '#f0f0f0', color: '#444',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: 'bold', minWidth: '44px', textAlign: 'center',
  },
  hourBtnActive: {
    padding: '8px 14px', backgroundColor: '#E94560', color: 'white',
    border: '1px solid #E94560', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: 'bold', minWidth: '44px', textAlign: 'center',
  },
  quickResolution: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' },
  quickBtn: {
    padding: '6px 12px', backgroundColor: '#e8f4fd', color: '#2c5282',
    border: '1px solid #bee3f8', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
  },
  resolutionNoteLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '6px' },
  resolutionNoteInput: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #ddd', fontSize: '13px',
    boxSizing: 'border-box', resize: 'vertical', marginBottom: '14px',
  },
  resolutionFinalRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
  },
  resolutionFinalLabel: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e' },
  resolveBtn: {
    padding: '12px 24px', backgroundColor: '#28a745', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
  },
  jobsList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  jobCard: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', border: '1px solid #e0e0e0' },
  jobCardHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '10px', gap: '12px',
  },
  jobWorkerName: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  jobDesc: { fontSize: '13px', color: '#555' },
  jobRevenue: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 },
  jobRevenueLabel: { fontSize: '11px', color: '#999' },
  jobRevenueValue: { fontSize: '20px', fontWeight: 'bold', color: '#28a745' },
  jobDetails: {
    display: 'flex', flexWrap: 'wrap', gap: '10px',
    marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #e0e0e0',
  },
  jobDetail: { fontSize: '12px', color: '#666' },
  jobCardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  jobContractor: { fontSize: '12px', color: '#999' },
  acceptedPill: {
    backgroundColor: '#d4edda', color: '#155724',
    padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
  },
  emptyState: { textAlign: 'center', padding: '60px' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyTitle: { fontSize: '20px', color: '#1a1a2e', marginBottom: '8px', fontWeight: 'bold' },
  emptyText: { fontSize: '14px', color: '#666' },
};

export default AdminDashboard;